import { useMemo } from 'react';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { PostalCodeField } from '@makemydeal/dr-shared-components/postal-code-field';
import { Dropdown } from '@makemydeal/dr-shared-components';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { FieldsRow } from '../sectionLayout.styles';
import type { AddressType } from './address.utils';

const PII_INPUT_ATTR = { textInput: { dataAttr: { className: 'pii' } } };

/** Prefix used for engine field paths when writing billing address values. */
const fieldPath = (type: AddressType, field: string): string => (type === 'billing' ? `billingAddress.${field}` : field);

type AddressFieldsProps = {
    type: AddressType;
};

/**
 * Edit form for a single address block (primary or billing).
 * Reads values and errors from CustomerFormContext and writes through
 * handleInputChange, which delegates to engine.setValue.
 *
 * For primary fields, uses flat keys ('streetAddress', 'city', …).
 * For billing fields, uses dotted engine paths ('billingAddress.streetAddress', …).
 */
const AddressFields = ({ type }: AddressFieldsProps) => {
    const { formValues, formErrors, handleInputChange, isLocked, isOptionalDeskingActive } = useCustomerCardContext();
    const stateOptions = useMemo(getStateCodesSelectInputValues, StateCodes);

    const requiredLabel = (label: string) => (isOptionalDeskingActive ? `${label} (required)` : label);

    const isBilling = type === 'billing';
    const values = isBilling ? formValues.billingAddress ?? {} : formValues;
    const errors = isBilling ? formErrors?.billingAddress ?? {} : formErrors;

    const onChange = (field: string, value: string) => handleInputChange(fieldPath(type, field), value);
    const testId = (name: string) => `customer-form-${type}-${name}`;
    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    return (
        <>
            <FieldsRow $columns={2} $mdColumns={1} $smColumns={1}>
                <TextInput
                    name="streetAddress"
                    displayLabel
                    label="Street Address"
                    value={(values.streetAddress as string) ?? ''}
                    onChange={(e) => onChange('streetAddress', e.target.value)}
                    hasError={Boolean(errors?.streetAddress?.hasError)}
                    errorMessage={errors?.streetAddress?.errorMessage ?? ''}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={testId('street-1-address')}
                    size="small"
                    disabled={isLocked}
                    slotProps={PII_INPUT_ATTR}
                />
                <TextInput
                    name="streetAddress2"
                    displayLabel
                    label="Street Address 2"
                    value={(values.streetAddress2 as string) ?? ''}
                    onChange={(e) => onChange('streetAddress2', e.target.value)}
                    hasError={Boolean(errors?.streetAddress2?.hasError)}
                    errorMessage={errors?.streetAddress2?.errorMessage ?? ''}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={testId('street-2-address')}
                    size="small"
                    disabled={isLocked}
                    slotProps={PII_INPUT_ATTR}
                />
            </FieldsRow>
            <FieldsRow>
                <TextInput
                    name="city"
                    displayLabel
                    label="City"
                    value={(values.city as string) ?? ''}
                    onChange={(e) => onChange('city', e.target.value)}
                    hasError={Boolean(errors?.city?.hasError)}
                    errorMessage={errors?.city?.errorMessage ?? ''}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={testId('city')}
                    size="small"
                    disabled={isLocked}
                    slotProps={PII_INPUT_ATTR}
                />
                <Dropdown
                    name="state"
                    displayLabel
                    label="State"
                    value={(values.state as string) ?? ''}
                    onChange={(e) => onChange('state', String(e.target.value))}
                    options={stateOptions}
                    hasError={Boolean(errors?.state?.hasError)}
                    errorMessage={errors?.state?.errorMessage ?? ''}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={testId('state')}
                    size="small"
                    allowRemoval={true}
                    disabled={isLocked}
                />
                <PostalCodeField
                    name="zip"
                    displayLabel
                    label={requiredLabel('Postal Code')}
                    value={(values.zip as string) ?? ''}
                    onChange={(e) => onChange('zip', e.target.value)}
                    hasError={Boolean(errors?.zip?.hasError)}
                    errorMessage={errors?.zip?.errorMessage ?? ''}
                    selectOnFocusSelector={selectOnFocusSelector}
                    required={!isOptionalDeskingActive}
                    data-testid={testId('postal-code')}
                    size="small"
                    disabled={isLocked}
                    slotProps={PII_INPUT_ATTR}
                />
                <TextInput
                    name="county"
                    displayLabel
                    label="County"
                    value={(values.county as string) ?? ''}
                    onChange={(e) => onChange('county', e.target.value)}
                    hasError={Boolean(errors?.county?.hasError)}
                    errorMessage={errors?.county?.errorMessage ?? ''}
                    selectOnFocusSelector={selectOnFocusSelector}
                    data-testid={testId('county')}
                    size="small"
                    disabled={isLocked}
                    slotProps={PII_INPUT_ATTR}
                />
            </FieldsRow>
        </>
    );
};

export default AddressFields;
